import { BookingType } from './LoadOffer'

export const enum BidStatus {
  Accepted = 'accepted',
  Awarded = 'awarded',
  Pending = 'pending',
  Refused = 'refused',
  Removed = 'removed',
  TimedOut = 'timed_out',
}

export type BidLoadStop = {
  stopIndex?: number
  type?: string
  city?: string
  state?: string
  country?: string
  zipcode?: string
  timezone?: string
  windowStart?: Date
  windowEnd?: Date
  distanceFromPreviousStop?: number
  facilityId?: string
  latitude?: number
  longitude?: number
  requirements?: Array<string>
  tags?: Array<string>
}

export type BidLoadDimensions = {
  length?: string
  height?: string
  width?: string
}

export type BidLoadRequirements = {
  tsa: boolean | null
  beer: boolean | null
  hvhr: boolean | null
  twic: boolean | null
  tarp: boolean | null
  ctpat: boolean | null
  teams: boolean | null
  frozen: boolean | null
  hazmat: boolean | null
  dunnage: boolean | null
  produce: boolean | null
  chemicals: boolean | null
  foodGrade: boolean | null
  ventedVans: boolean | null
  drop_trailer: boolean | null
  pharmaceuticals: boolean | null
  dontCallDriver: boolean | null
}

export type BidLoad = {
  uuid?: string
  createdAt?: string
  refNumber?: string
  directFromShipper?: boolean
  loadCandidate?: boolean
  shipperId?: string
  commodity?: string
  temperature?: null
  weight?: number
  mileage?: string
  customerRequirements?: Array<string>
  equipmentType?: string
  intermodal?: null
  stops?: Array<BidLoadStop>
  teamDriver?: boolean
  dimensions?: BidLoadDimensions
  requirements?: BidLoadRequirements
  equipmentSubtypes?: Array<string>
  shipperMarket?: string
  tags?: Array<string>
  deadhead?: string
  mode?: string
  mustArriveByDate?: string
}

export type BidOffer = {
  uuid: string
  price: number
}

export type Bid = {
  uuid: string
  price: number
  bookPrice: number
  createdAt: Date
  expiresAt: Date
  expiredAt?: Nullable<Date>
  status: BidStatus
  source: Nullable<string>
  load: BidLoad
  offer: BidOffer
  refuseReason: Nullable<string>
  counteredBid?: Bid
}

type ShouldShowBidCountdownParams = {
  carrier: {
    bid_probability: boolean
  }
  countdownHasFinished: boolean
  nextBidTime: string | undefined
}

export function shouldShowBidCountdown({
  carrier,
  countdownHasFinished,
  nextBidTime,
}: ShouldShowBidCountdownParams) {
  const carrierHasBidProbability = carrier?.bid_probability ?? false
  return carrierHasBidProbability && Boolean(nextBidTime) && !countdownHasFinished
}

type ShouldDisableBidParams = {
  carrier: {
    bid_enabled: boolean
    has_active_dnu: boolean
  }
  activeBid: {
    status: BidStatus
  }
  showBidCountdown: boolean
}

export function shouldDisableBid({ carrier, activeBid, showBidCountdown }: ShouldDisableBidParams) {
  const carrierHasActiveDNU = carrier?.has_active_dnu ?? false
  const carrierHasBidEnabled = carrier?.bid_enabled ?? false
  const hasRefusedOrTimedOutActiveBid =
    (activeBid && [BidStatus.Refused, BidStatus.TimedOut].includes(activeBid.status)) || false

  if (!carrierHasBidEnabled) return true
  if (carrierHasActiveDNU) return true
  if (hasRefusedOrTimedOutActiveBid) return true
  if (showBidCountdown) return true
  return false
}

export function getBidBookingType(bid: { status: BidStatus; countered_bid: string }): BookingType {
  if (bid?.countered_bid) {
    return BookingType.BidCounter
  }

  if (bid?.status === BidStatus.Refused) {
    return BookingType.BidTooHigh
  }

  if (bid?.status === BidStatus.Awarded) {
    return BookingType.BidAward
  }

  return BookingType.BookNow
}

export function shouldShowBidPrices(bookingType: BookingType) {
  return [BookingType.BidCounter, BookingType.BidTooHigh].includes(bookingType)
}

export function isBidAwarded(bid?: Nullable<Bid>): boolean {
  return bid?.status === BidStatus.Awarded
}

export function shouldRemoveBidOnBookingComplete(bookingType: BookingType): boolean {
  return bookingType === BookingType.BidTooHigh
}

export function hasBidPriceChanged(bid?: Nullable<Bid>): boolean {
  if (!bid) return false
  return Boolean(bid.bookPrice && bid.price && bid.price !== bid.bookPrice)
}
